import { useCallback, useMemo } from "react";
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import { ColumnConfigState } from "@racoon/ui";
import { useMediaQuery } from "./useMediaQuery";

/**
 * The organization's saved tables with `tables` put in place of the ones of the
 * same names, the others kept. The whole `table_columns_config` is sent on every
 * update, so every writer merges through this.
 */
export const replaceTables = <
  Saved extends { tableName: string },
  New extends { tableName: string },
>(
  saved: Saved[] | undefined,
  tables: New[],
): (Saved | New)[] => {
  const names = new Set(tables.map((table) => table.tableName));
  return [...(saved ?? []).filter((table) => !names.has(table.tableName)), ...tables];
};

export function useTableSettings(tableName: string, serviceProviderId?: string | number) {
  const isMobileOrTablet = useMediaQuery("(max-width: 1024px)");
  const deviceSuffix = isMobileOrTablet ? "_mobile" : "";
  const effectiveTableName = `${tableName}${deviceSuffix}`;

  // Settings resolve to the current user's own org (gRPC getSetting, auth context).
  const { data: settings, isLoading } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !serviceProviderId,
    },
  );

  const [updateOrganizationSettings] = useUpdateOrganizationSettingsMutation();

  const currentTableConfig = useMemo(() => {
    if (!settings?.tableColumnsConfig?.tables) {
      return undefined;
    }
    return settings.tableColumnsConfig.tables.find((t) => t.tableName === effectiveTableName);
  }, [settings, effectiveTableName]);

  const columnConfig = useMemo<ColumnConfigState | undefined>(() => {
    if (!currentTableConfig) return undefined;
    return {
      columns: currentTableConfig.columns.map((col) => ({
        id: col.columnName,
        isVisible: !col.isHidden,
        width: col.width,
        order: col.order,
      })),
    };
  }, [currentTableConfig]);

  const handleColumnConfigChange = useCallback(
    (config: ColumnConfigState) => {
      if (!settings) return;

      const newColumns = config.columns.map((c) => ({
        columnName: c.id,
        isHidden: !c.isVisible,
        width: typeof c.width === "number" ? c.width : undefined,
        order: c.order,
      }));

      updateOrganizationSettings({
        id: settings.id,
        tableColumnsConfig: {
          tables: replaceTables(settings.tableColumnsConfig?.tables, [
            { tableName: effectiveTableName, columns: newColumns },
          ]),
        },
      });
    },
    [settings, updateOrganizationSettings, effectiveTableName],
  );

  return {
    columnConfig,
    handleColumnConfigChange,
    settings, // Return full settings if needed (e.g. for ai_enabled)
    isLoading,
  };
}
